import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { collection, query, where, getDocs, setDoc, doc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { UserProfile, ClubEvent, DepartmentReport, CompiledMasterLog } from '../../types';
import { format, subMonths } from 'date-fns';
import { Database, Download, Lock } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface MasterLogViewerProps {
  isAdmin: boolean;
  events: ClubEvent[];
  currentUserProfile: UserProfile | null;
}

export default function MasterLogViewer({ isAdmin, events, currentUserProfile }: MasterLogViewerProps) {
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [reports, setReports] = useState<DepartmentReport[]>([]);
  const [compiledLog, setCompiledLog] = useState<CompiledMasterLog | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch Department Reports for the selected month
        const rQ = query(collection(db, 'reports'), where('month', '==', selectedMonth));
        const rSnap = await getDocs(rQ);
        const reportsData = rSnap.docs.map(d => d.data() as DepartmentReport);
        
        const deptOrder = [
          'command',
          'xo',
          'operations',
          'ops',
          'communications',
          'comms',
          'engineering',
          'sciences',
          'medical',
          'security',
          'tactical',
          'navigation/helm',
          'navigation',
          'helm',
          'other'
        ];
        
        reportsData.sort((a, b) => {
          const indexA = deptOrder.indexOf((a.departmentId || '').toLowerCase());
          const indexB = deptOrder.indexOf((b.departmentId || '').toLowerCase());
          
          const weightA = indexA !== -1 ? indexA : 999;
          const weightB = indexB !== -1 ? indexB : 999;
          
          if (weightA !== weightB) return weightA - weightB;
          return (a.departmentId || '').localeCompare(b.departmentId || '');
        });
        
        setReports(reportsData);

        // Fetch Compiled Log for the selected month
        const cQ = query(collection(db, 'compiledReports'), where('id', '==', selectedMonth));
        const cSnap = await getDocs(cQ);
        if (!cSnap.empty) {
          setCompiledLog(cSnap.docs[0].data() as CompiledMasterLog);
        } else {
          setCompiledLog(null);
        }
      } catch (err) {
        console.error("Failed to load master log data", err);
      }
      setLoading(false);
    };
    fetchData();
  }, [selectedMonth]);

  const monthOptions = Array.from({ length: 60 }).map((_, i) => {
    const d = subMonths(new Date(), i);
    return format(d, 'yyyy-MM');
  });

  const monthEvents = events.filter(e => {
    if (!e.startDate) return false;
    return e.startDate.startsWith(selectedMonth);
  });

  const generateHtmlContent = () => {
    let html = `
      <div style="font-family: sans-serif; color: white; background: black; padding: 40px;">
        <h1 style="color: #ff9900; font-size: 32px; border-bottom: 2px solid #99ccff; padding-bottom: 10px; margin-bottom: 30px;">
          USS ZAVALA MASTER LOG - ${selectedMonth}
        </h1>
        
        <h2 style="color: #99ccff; margin-top: 30px;">SHIP MANIFEST (EVENTS)</h2>
        <ul style="list-style-type: none; padding-left: 0;">
    `;
    
    if (monthEvents.length === 0) {
      html += `<li><em style="color: #888;">No events recorded.</em></li>`;
    } else {
      monthEvents.forEach(e => {
        html += `<li style="margin-bottom: 10px;"><strong>${e.startDate.split('T')[0]}:</strong> ${e.name} (${e.type})</li>`;
      });
    }

    html += `</ul><h2 style="color: #cc99cc; margin-top: 40px; border-bottom: 1px solid #cc99cc; padding-bottom: 5px;">DEPARTMENTAL REPORTS</h2>`;

    if (reports.length === 0) {
      html += `<p><em style="color: #888;">No departmental reports submitted yet.</em></p>`;
    } else {
      reports.forEach(r => {
        html += `
          <div style="margin-top: 20px; background: rgba(255,255,255,0.05); padding: 15px; border-left: 4px solid #ffcc00;">
            <h3 style="color: #ffcc00; margin-top: 0;">${r.departmentId.toUpperCase()}</h3>
            <p style="white-space: pre-wrap;">${r.content}</p>
            ${r.fileData ? `<p style="color: #99ccff; font-size: 12px;">[Attachment Included]</p>` : ''}
          </div>
        `;
      });
    }

    html += `</div>`;
    return html;
  };

  const handleCompile = async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const newVersion = {
        versionId: new Date().getTime().toString(),
        content: generateHtmlContent(),
        compiledAt: new Date().toISOString(),
        compiledByUid: currentUserProfile?.uid || 'System'
      };

      const newVersions = compiledLog ? [...compiledLog.versions, newVersion] : [newVersion];

      const newDoc: CompiledMasterLog = {
        id: selectedMonth,
        versions: newVersions,
        currentVersionId: newVersion.versionId
      };

      await setDoc(doc(db, 'compiledReports', selectedMonth), newDoc);
      setCompiledLog(newDoc);
    } catch (err) {
      console.error("Compile failed", err);
    }
    setLoading(false);
  };

  const handleHistoricalUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isAdmin) return;
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 1024 * 1024) {
      alert("Historical PDF is too large. Maximum size is 1MB for direct database storage.");
      return;
    }

    setLoading(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64Pdf = reader.result as string;
      try {
        const newVersion = {
          versionId: new Date().getTime().toString(),
          content: `<div style="padding: 40px; text-align: center; color: white; background: black; font-family: sans-serif;"><h2 style="color: #99ccff; font-size: 24px; margin-bottom: 20px;">Historical Archive Upload</h2><p>This month's log was directly backfilled with a historical PDF.</p><p style="margin-top: 30px;"><a href="${base64Pdf}" download="Historical_Master_Log_${selectedMonth}.pdf" style="padding: 10px 20px; background: #ffcc00; color: black; text-decoration: none; font-weight: bold; border-radius: 4px;">Download Historical PDF</a></p></div>`,
          compiledAt: new Date().toISOString(),
          compiledByUid: currentUserProfile?.uid || 'System'
        };

        const newVersions = compiledLog ? [...compiledLog.versions, newVersion] : [newVersion];

        const newDoc: CompiledMasterLog = {
          id: selectedMonth,
          versions: newVersions,
          currentVersionId: newVersion.versionId
        };

        await setDoc(doc(db, 'compiledReports', selectedMonth), newDoc);
        setCompiledLog(newDoc);
      } catch (err) {
        console.error("Upload failed", err);
      }
      setLoading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleInjectHistoric = async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const logsToInject = [
        { month: '2026-03', file: 'USS-ZAVALA-LOG-2026-03.pdf' },
        { month: '2026-04', file: 'USS-ZAVALA-LOG-2026-04.pdf' }
      ];

      for (const log of logsToInject) {
        const newVersion = {
          versionId: new Date().getTime().toString(),
          content: `<div style="padding: 40px; text-align: center; color: white; background: black; font-family: sans-serif;"><h2 style="color: #99ccff; font-size: 24px; margin-bottom: 20px;">Historical Archive - ${log.month}</h2><p>This month's log is archived as a static PDF.</p><p style="margin-top: 30px;"><a href="/historic_logs/${log.file}" target="_blank" style="padding: 10px 20px; background: #ffcc00; color: black; text-decoration: none; font-weight: bold; border-radius: 4px;">Open Historical PDF</a></p></div>`,
          compiledAt: new Date().toISOString(),
          compiledByUid: currentUserProfile?.uid || 'System'
        };

        const newDoc: CompiledMasterLog = {
          id: log.month,
          versions: [newVersion],
          currentVersionId: newVersion.versionId
        };
        await setDoc(doc(db, 'compiledReports', log.month), newDoc);
      }
      alert("Historic logs for March and April injected successfully!");
      if (selectedMonth === '2026-03' || selectedMonth === '2026-04') {
        window.location.reload();
      }
    } catch (e: any) {
      alert("Error injecting logs: " + e.message);
    }
    setLoading(false);
  };

  const handleDownloadPdf = async () => {
    if (!compiledLog) return;
    const rawContent = compiledLog.versions.find(v => v.versionId === compiledLog.currentVersionId)?.content || '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = DOMPurify.sanitize(rawContent);
    tempDiv.className = 'bg-[var(--background)] text-[var(--foreground)] p-8 w-[800px] absolute -left-[9999px]';
    document.body.appendChild(tempDiv);

    try {
      const canvas = await html2canvas(tempDiv, { scale: 2, useCORS: true, backgroundColor: '#000000' });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`USS_Zavala_MasterLog_${selectedMonth}.pdf`);
    } catch (e) {
      console.error("Failed to generate PDF", e);
    } finally {
      document.body.removeChild(tempDiv);
    }
  };

  return (
    <div className="flex flex-col h-full gap-4">
      <div className="flex justify-between items-center bg-white/5 border border-white/10 p-4 rounded-xl">
        <div className="flex items-center gap-4">
          <Database className="w-5 h-5 text-lcars-blue" />
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="bg-[var(--background)]/50 border border-white/20 rounded px-4 py-2 text-[var(--foreground)] outline-none focus:border-lcars-blue font-bold"
          >
            {monthOptions.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>

        <div className="flex gap-2">
          {isAdmin && (
            <>
              <button
                onClick={handleCompile}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-lcars-pink text-black font-bold rounded hover:opacity-80 disabled:opacity-50 text-xs"
              >
                <Lock className="w-4 h-4" />
                COMPILE ARCHIVE
              </button>
              <label className="flex items-center gap-2 px-4 py-2 bg-white/10 text-[var(--foreground)] font-bold rounded cursor-pointer hover:bg-white/20 disabled:opacity-50 text-xs border border-white/10">
                <Download className="w-4 h-4 rotate-180" />
                UPLOAD HISTORICAL PDF
                <input id="historicUpload" name="historicUpload" type="file" accept=".pdf" className="hidden" onChange={handleHistoricalUpload} disabled={loading} />
              </label>
              <button
                onClick={handleInjectHistoric}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-black font-bold rounded hover:bg-yellow-400 disabled:opacity-50 text-xs animate-pulse"
              >
                <Lock className="w-4 h-4" />
                INJECT HISTORIC LOGS
              </button>
            </>
          )}
          
          {compiledLog && (
            <button
              onClick={handleDownloadPdf}
              className="flex items-center gap-2 px-4 py-2 bg-lcars-blue text-black font-bold rounded hover:opacity-80 text-xs"
            >
              <Download className="w-4 h-4" />
              EXPORT PDF
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 bg-white/5 border border-white/10 rounded-xl p-6 overflow-y-auto">
        {compiledLog ? (
          <div 
            className="prose prose-invert max-w-none"
            dangerouslySetInnerHTML={{ 
              __html: DOMPurify.sanitize(compiledLog.versions.find(v => v.versionId === compiledLog.currentVersionId)?.content || 'No content found.') 
            }} 
          />
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-[var(--foreground)]/40 gap-4">
            <div className="text-xl font-bold tracking-widest text-lcars-orange uppercase">Uncompiled Working Log</div>
            <p className="text-sm max-w-md text-center">
              The Master Log for {selectedMonth} has not been compiled yet. Command must compile the log before the official record can be exported or archived.
            </p>
            <div className="mt-8 p-6 bg-[var(--background)]/50 rounded-xl w-full max-w-2xl border border-white/10">
              <h3 className="text-lcars-gold font-bold mb-4">Current Submissions ({reports.length})</h3>
              {reports.map(r => (
                <div key={r.id} className="text-sm py-2 border-b border-white/5 flex justify-between">
                  <span className="text-lcars-blue">{r.departmentId}</span>
                  <span className="opacity-60">{format(new Date(r.submittedAt), 'MMM dd, HH:mm')}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
